import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: { padding: theme.spacing(2), margin: theme.spacing(1) },
  profileCard: {
    cursor: "pointer", textAlign: "center", padding: theme.spacing(2),
    transition: "all 0.2s", border: "3px solid transparent",
    "&:hover": { transform: "translateY(-2px)", boxShadow: theme.shadows[4] },
  },
  profileCardActive: { borderColor: "#10aa62", backgroundColor: "#f0fdf4" },
  profileAvatar: {
    width: 56, height: 56, margin: "0 auto 8px", fontSize: 20, fontWeight: 700,
  },
  userCard: {
    display: "flex", alignItems: "center", gap: 16,
    padding: theme.spacing(2), marginBottom: theme.spacing(1),
  },
  userAvatar: { width: 44, height: 44, fontSize: 18, fontWeight: 700 },
  enterBtn: {
    backgroundColor: "#10aa62", color: "#fff",
    "&:hover": { backgroundColor: "#0d8c52" },
  },
  searchField: { marginBottom: theme.spacing(2) },
}));

const COLORS = { admin: "#e74c3c", supervisor: "#3498db", cliente: "#2ecc71", vendedor: "#f39c12" };

const Espejo = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/admin/mirror/profiles");
        setProfiles(data.profiles || []);
      } catch (err) { toastError(err); }
    };
    load();
  }, []);

  const selectProfile = async (profileName) => {
    setSelectedProfile(profileName);
    setLoading(true);
    try {
      const { data } = await api.get("/admin/mirror/users", { params: { profile: profileName } });
      setUsers(data.users || []);
    } catch (err) { toastError(err); }
    setLoading(false);
  };

  const impersonate = async (userId) => {
    try {
      const { data } = await api.post("/admin/mirror/impersonate/" + userId);
      if (data.token) {
        // Save admin session so we can return later
        localStorage.setItem("admin_token", localStorage.getItem("token"));
        localStorage.setItem("admin_userId", localStorage.getItem("userId"));
        localStorage.setItem("admin_companyId", localStorage.getItem("companyId"));
        localStorage.setItem("admin_profile", localStorage.getItem("profile"));
        localStorage.setItem("impersonating", "true");

        localStorage.setItem("token", data.token);
        localStorage.setItem("userId", data.user.id);
        localStorage.setItem("companyId", data.user.companyId);
        localStorage.setItem("profile", data.user.profile);
        window.location.href = "/";
      }
    } catch (err) { toastError(err); }
  };

  const filtered = search
    ? users.filter(u => (u.name || "").toLowerCase().includes(search.toLowerCase())
        || (u.email || "").toLowerCase().includes(search.toLowerCase())
        || (u.phone || "").includes(search))
    : users;

  if (user.profile !== "admin" && !user.super) {
    return (
      <MainContainer>
        <MainHeader><Title>Acceso denegado</Title></MainHeader>
        <Paper style={{padding: 40, textAlign: "center"}}>
          <Typography>Solo administradores pueden usar el Espejo.</Typography>
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Espejo - Impersonar Usuarios</Title>
      </MainHeader>
      <Paper className={classes.mainPaper}>
        <Typography variant="subtitle1" gutterBottom><b>1) Selecciona un perfil</b></Typography>
        <Grid container spacing={2} style={{marginBottom: 24}}>
          {profiles.map((p) => (
            <Grid item xs={6} sm={3} key={p.name}>
              <Card
                className={`${classes.profileCard} ${selectedProfile === p.name ? classes.profileCardActive : ""}`}
                onClick={() => selectProfile(p.name)}
              >
                <CardContent>
                  <Avatar className={classes.profileAvatar} style={{backgroundColor: COLORS[p.name] || "#6b7280"}}>
                    {p.name.substring(0,3).toUpperCase()}
                  </Avatar>
                  <Typography variant="subtitle1" style={{textTransform:"capitalize",fontWeight:600}}>{p.name}</Typography>
                  <Typography variant="caption" color="textSecondary">{p.user_count} usuarios</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="subtitle1" gutterBottom><b>2) Usuarios del perfil seleccionado</b></Typography>
        <TextField
          className={classes.searchField}
          fullWidth variant="outlined" size="small"
          placeholder="Buscar por nombre, correo o telefono"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />

        {!selectedProfile && (
          <Typography color="textSecondary" align="center" style={{padding:30}}>
            Selecciona un perfil para ver los usuarios
          </Typography>
        )}
        {loading && <Typography align="center" style={{padding:30}}>Cargando...</Typography>}

        <Grid container spacing={2}>
          {filtered.map((u) => (
            <Grid item xs={12} sm={6} md={4} key={u.id}>
              <Card className={classes.userCard}>
                <Avatar className={classes.userAvatar} style={{backgroundColor: COLORS[u.profile] || "#6b7280"}}>
                  {(u.name || "?")[0].toUpperCase()}
                </Avatar>
                <div style={{flex:1,minWidth:0}}>
                  <Typography variant="subtitle2" noWrap>{u.name}</Typography>
                  <Typography variant="caption" color="textSecondary" noWrap>{u.email}</Typography>
                  <div><Chip size="small" label={u.profile} style={{fontSize:10,height:18,marginTop:4}} /></div>
                </div>
                <Button size="small" variant="contained" className={classes.enterBtn}
                  startIcon={<SwapHorizIcon />} onClick={() => impersonate(u.id)}>
                  Entrar
                </Button>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default Espejo;
